import * as Sentry from '@sentry/react';

const SENTRY_DSN = "https://803ff5f06ee271ec8a10be973c73ebac@o4511122385731584.ingest.us.sentry.io/4511122584633344";
const APP_ENV = import.meta.env.VITE_APP_ENV || 'production';

export function initSentry() {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    
    // Note: sendDefaultPii is false for FERPA compliance
    sendDefaultPii: false,
    
    // Performance monitoring
    tracesSampleRate: 0.2, // 20% of transactions
    
    // Session replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Mask text for privacy (FERPA)
        blockAllMedia: true, // Block media for privacy
      }),
    ],

    // Filter out sensitive data
    beforeSend(event) {
      // Remove any potential PII from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            // Remove any keys that might contain document content
            const sensitiveKeys = ['text', 'content', 'body', 'document', 'file'];
            sensitiveKeys.forEach(key => {
              if (breadcrumb.data && key in breadcrumb.data) {
                breadcrumb.data[key] = '[REDACTED]';
              }
            });
          }
          return breadcrumb;
        });
      }
      return event;
    },

    // Don't send errors in development by default
    enabled: APP_ENV === 'production' || !!SENTRY_DSN,
  });
}

// Export Sentry for use throughout the app
export { Sentry };

// Helper to capture errors with context
export function captureProcessingError(
  error: Error,
  context: {
    fileType?: string;
    fileSize?: number;
    pageCount?: number;
    tier?: string;
    errorStage?: 'upload' | 'validation' | 'extraction' | 'ocr' | 'gemini_call' | 'redaction' | 'output';
    processingMode?: 'text' | 'batch';
  }
): string {
  const eventId = Sentry.captureException(error, {
    tags: {
      file_type: context.fileType,
      tier: context.tier,
      error_stage: context.errorStage,
      processing_mode: context.processingMode,
    },
    extra: {
      file_size: context.fileSize,
      page_count: context.pageCount,
    },
  });
  
  return eventId;
}

// Show user feedback dialog
export function showFeedbackDialog(eventId: string) {
  Sentry.showReportDialog({
    eventId,
    title: 'Report an Issue',
    subtitle: 'Help us fix this problem.',
    subtitle2: 'Your feedback helps improve Redact AI.',
    labelComments: 'What happened?',
    labelSubmit: 'Submit Report',
  });
}
